// Pragmatic port of RosterView. See ../CardsViewPragmatic/README.md for the
// full rationale and known HTML5-DnD limitations (dotted-rectangle first-frame
// artifact, Chromium "+" cursor flash). The same trade-offs apply here.

import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { format, getISOWeek } from 'date-fns';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { Box, styled, Tooltip, Typography } from '@mui/material';
import Chip from '@sdb/ui/atoms/Chip';

// components
import { LoadingOverlay } from 'components/shared';
import DraggableCardPragmatic from '../CardsViewPragmatic/DraggableCardPragmatic/DraggableCardPragmatic';
import ActivityPanelPragmatic from '../CardsViewPragmatic/ActivityPanelPragmatic/ActivityPanelPragmatic';
import DragPortalPragmatic from '../CardsViewPragmatic/DragPortalPragmatic/DragPortalPragmatic';
import FrozenDropCardPragmatic from '../CardsViewPragmatic/FrozenDropCardPragmatic';
import ToPlanDropzonePragmatic from '../CardsViewPragmatic/ToPlanDropzonePragmatic/ToPlanDropzonePragmatic';
import SortedToPlanListPragmatic from '../CardsViewPragmatic/SortedToPlanListPragmatic/SortedToPlanListPragmatic';
import DroppableCellPragmatic from './DroppableCellPragmatic';
import AbsencePragmatic from './AbsencePragmatic';
import AvailabilityPragmatic from './AvailabilityPragmatic';
import { PopOverConfirmationContext } from '../PopOverConfirmationContext';
import EventCard from '../RosterView/EventCard/EventCard';
import EventPlaceholder from '../RosterView/EventPlaceholder/EventPlaceholder';
import KidsTable from '../RosterView/KidsTable';
import TaskList from 'components/shared/TaskList/TaskList';

// hooks
import { useNavParams, useSelector } from 'hooks';
import { useRecoilState, useRecoilValue } from 'recoil';
import type { CopyModeReturn } from '../hooks/useCopyMode';
import useCopyMode from '../hooks/useCopyMode';
import { DROP_LOADING_OVERLAY_Z_INDEX } from '../frozenDrop.constants';
import useConnectScheduleAction from '../hooks/useConnectScheduleAction';
import { useFrozenDrop } from '../hooks/useFrozenDrop';
import type { UsePlanningReturn } from '../hooks/usePlanning';
import usePlanningEntity from '../hooks/usePlanningEntity';
import useSelectedDate from '../hooks/useSelectedDate';
import { useDateNavigationStore } from 'components/shared/DateNavigation/DateNavigation.stores';

// styles
import { GRAY_LIGHT_TERTIARY, PRIMARY, WHITE } from 'constants/colors';
import { CARD_WIDTH } from 'constants/widths';

// utils
import type { APNotification } from '../utils';
import {
  appendCorrelationToken,
  findItem,
  getAbsencesOrAvailabilitiesByWeekDay,
  getConflictingEvents,
  getEntityPreferenceKey,
  getEventsByWeekDay,
  handlePlanAppointments as handlePlanAppointmentsUtil,
  isWithinDay,
  notificationsToHashTable,
  planningEntitiesToEvents,
  sourceViewportContext,
  unassignAppointment as unassignAppointmentUtil
} from '../utils';
import { formatHours, getWeekInterval, getWorkweekInterval } from 'utils/date';
import { getRosterDays } from 'components/shared/roster/utils';
import { showErrorSnackbar } from 'components/shared/Snackbar';
import { getCurrentlyLoggedInEmployeeId } from 'utils/misc';

// types
import type {
  AbsenceResponse,
  AvailabilityResponse,
  PlanningEntityIdentifier,
  PlanningEntityType
} from 'generated';
import type { PlanAppointmentsRequest } from 'generated';
import type { Event, Schedule } from '../types';
import type { EditAppointmentType } from 'RestAPIs/activities/types';
import type { ConnectAppointmentGroupingPayload } from 'RestAPIs/activities';
import type { RosterDay } from 'components/shared/roster/types';
import type { View } from '../enums';

// atoms
import {
  dialogManagerActiveAtom,
  frozenCardDropAtom,
  planResponsePlanningEntityIdentifiersAtom,
  planUnitsAtom,
  shouldShowPopoverDialogAtom
} from '../atoms';

// mutations
import { mutations } from 'RestAPIs/rapini';
import { DialogType } from '../components/DialogManager/types';
import { ZoomLevel } from '../components/Header/ZoomLevelMenu.enums';

// Force "move" cursor for the whole window during HTML5 drags. Registered at
// module load (capture phase, idempotent via the window flag) — mirrors the
// equivalent install in `CardsViewPragmatic.tsx`; see its README for details.
declare global {
  interface Window {
    __rosterViewPragmaticDragListenersInstalled?: boolean;
  }
}
if (
  typeof document !== 'undefined' &&
  !window.__rosterViewPragmaticDragListenersInstalled
) {
  window.__rosterViewPragmaticDragListenersInstalled = true;
  document.addEventListener(
    'dragstart',
    (event: DragEvent) => {
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move';
      }
    },
    { capture: true }
  );
  const acceptAsMove = (event: DragEvent) => {
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
    event.preventDefault();
  };
  document.addEventListener('dragenter', acceptAsMove, { capture: true });
  document.addEventListener('dragover', acceptAsMove, { capture: true });
}

dayjs.extend(isoWeek);

const Table = styled('table')`
  width: 100vw;
  border-collapse: collapse;
  table-layout: fixed;
`;

const TableHeaderDateRow = styled('tr')`
  position: sticky;
  top: 0;
  z-index: 40;
  height: 50px;
  background-color: ${WHITE};
  box-shadow: inset 0 -1px 0 ${PRIMARY};
`;

const TableHeaderDateCell = styled('th')`
  min-width: ${CARD_WIDTH}px;
`;

const TableCell = styled('td')`
  min-width: ${CARD_WIDTH}px;
  height: 1px; // See: https://stackoverflow.com/a/3542470
  padding: 4px;
`;

function getSubtitle(
  event: Event,
  planningEntity: PlanningEntityType,
  showEvents: boolean
) {
  switch (planningEntity) {
    case 'CLIENT':
      return event?.employees?.[0]?.nameInformal;
    case 'EMPLOYEE':
    case 'EMPLOYMENT':
      return showEvents
        ? `${event?.clients?.[0]?.firstName ?? ''} ${
            event?.clients?.[0]?.lastName ?? ''
          }`
        : '';
    case 'WORKPLACE':
      return event?.employees?.[0]?.nameInformal;
    default:
      return null;
  }
}

export default function RosterViewPragmatic({
  kidsRatio,
  isLoading,
  endDate,
  currentPlanningEntityIdentifiers,
  planningEntities,
  setPlanningEntities,
  clonedPlanningEntities,
  setClonedPlanningEntities,
  connectAppointmentGrouping,
  connectSchedule,
  planAppointments,
  revertAction,
  showEvents,
  isMyActivities
}: UsePlanningReturn) {
  'use memo';
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [activeEntityContainingSelection, setActiveEntityContainingSelection] =
    useState<string | null>(null);
  const dateNavigationPeriod = useDateNavigationStore(({ period }) => period);
  const [shouldShowPopoverDialog, setShouldShowPopoverDialog] = useRecoilState(
    shouldShowPopoverDialogAtom
  );
  const selectedPlanUnits = useRecoilValue(planUnitsAtom);
  const [pendingDropPosition, setPendingDropPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);

  const [taskListIsOpen, setTaskListIsOpen] = useState<boolean>(true);

  const multiplePlanUnitsSelected = selectedPlanUnits.length > 1;

  const [activeEvent, setActiveEvent] = useState<Event | null>(null);

  const { hasKidsPlanAccess, hasActivityPlanningEditAccess } = useSelector(
    ({ grants }) => grants
  );
  const { params } = useNavParams();
  const view = params?.view as View;

  const [planningEntityTypePreference] = usePlanningEntity();
  const planningEntityType =
    planningEntityTypePreference[getEntityPreferenceKey(view)];
  const [, setPlanResponsePlanningEntityIdentifiers] = useRecoilState(
    planResponsePlanningEntityIdentifiersAtom
  );
  const [selectedDate] = useSelectedDate();
  const { activityPlanningOnlyPlanned, defaultZoomLevel } = useSelector(
    (state) => state.preferences
  );
  const isDetailed =
    (defaultZoomLevel ?? ZoomLevel.STANDARD) === ZoomLevel.DETAILED;
  const startDate = dayjs(selectedDate).startOf('isoWeek').format('YYYY-MM-DD');

  const isWorkweek = dateNavigationPeriod === 'workweek';
  const weekLength = isWorkweek ? 5 : 7;
  const holidays = useSelector((state) => state.holidays);
  const weekdays = getRosterDays(
    isWorkweek
      ? getWorkweekInterval(selectedDate)
      : getWeekInterval(selectedDate),
    holidays
  );
  const weekNumber = getISOWeek(selectedDate);

  const copyMode: CopyModeReturn = useCopyMode();
  const { endCopyMode } = copyMode;

  // Intentionally mount-only: we want to reset copy mode once when the user
  // navigates INTO this view, not every time `useCopyMode` returns a fresh
  // function identity (which it does on every render). A ref is used so the
  // mount-time call still resolves the most recent `endCopyMode` closure.
  const endCopyModeRef = useRef(endCopyMode);
  // Ref updated in an effect (never during render) so the React Compiler can
  // optimize this component; the mount-only effect below still resolves the
  // latest closure.
  useEffect(() => {
    endCopyModeRef.current = endCopyMode;
  });
  useEffect(() => {
    endCopyModeRef.current();
  }, []);

  // The React Compiler memoises this so hover/selection/drag changes don't redo
  // the filter+map+sort. `getCurrentlyLoggedInEmployeeId()` reads Redux
  // imperatively, not reactive.
  const normalizedData = planningEntities
    ? Object.values(planningEntities)
        ?.filter(
          (planningEntity) =>
            (!activityPlanningOnlyPlanned ||
              planningEntity.events.length ||
              planningEntity.schedules.length) &&
            planningEntity.type !== 'ORGANIZATIONAL_UNIT_TO_PLAN'
        )
        .filter((planningEntity) => {
          return planningEntity.type !== 'EMPLOYEE';
        })
        .map((item) => {
          const itemScheduleEvents = (item.schedules ?? [])
            .map((schedule) => schedule.events)
            .flat();
          const allRawEvents = [...(item.events ?? []), ...itemScheduleEvents];
          const events = getEventsByWeekDay(
            isMyActivities
              ? allRawEvents.filter(
                  (event) =>
                    event?.employees?.some(
                      ({ id }) => id === +getCurrentlyLoggedInEmployeeId()
                    )
                )
              : allRawEvents,
            dateNavigationPeriod
          );

          const schedules = getEventsByWeekDay(
            item.schedules ?? [],
            dateNavigationPeriod
          );
          const rowCount = Object.values(
            item.type === 'EMPLOYMENT' || item.type === 'EMPLOYEE'
              ? schedules
              : events
          ).reduce(
            (acc, cur) => (cur.length > acc.length ? cur : acc),
            []
          ).length;

          const absences =
            item.absences && item.absences.length > 0
              ? getAbsencesOrAvailabilitiesByWeekDay(
                  item.absences,
                  dateNavigationPeriod
                )
              : [];

          const availabilities =
            item.availabilities && item.availabilities.length > 0
              ? getAbsencesOrAvailabilitiesByWeekDay(
                  item.availabilities,
                  dateNavigationPeriod
                )
              : [];
          const metrics = item.metrics ?? undefined;

          const newItem = {
            ...item,
            absences,
            availabilities,
            events,
            schedules,
            rowCount,
            metrics
          };

          return newItem;
        })
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    : [];

  // Refs and state that the cross-cutting callbacks below need to read/write
  // are declared FIRST so the closures don't depend on TDZ-fragile ordering.
  const containerRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [payload, setPayload] = useState<
    PlanAppointmentsRequest | ConnectAppointmentGroupingPayload
  >();
  // A single subscription gives both the setter and the value used below to
  // decide whether the warning dialog is holding the frozen card.
  const [warningDialogState, setDialogState] = useRecoilState(
    dialogManagerActiveAtom
  );

  // `revertAction` is unstable (no useCallback in usePlanning); ref-latch it so
  // onMutationError -> sendConnectSchedule stays stable for the
  // compiler-memoized ActivityPanelPragmatic.
  const revertActionRef = useRef(revertAction);
  useEffect(() => {
    revertActionRef.current = revertAction;
  });

  const { mutateAsync: unassignAppointmentAsync } =
    mutations.useRemoveAssignmentFromAppointments();
  const [
    isLoadingUnassignAppointmentFromEmployee,
    setIsLoadingUnassignAppointmentFromEmployee
  ] = useState<boolean>(false);

  const loading = isLoading || isLoadingUnassignAppointmentFromEmployee;

  // A recurring drop shows the "this / all occurrences" confirmation popover
  // before any API call fires. The action isn't finished until the user picks
  // an option, so keep the card frozen while that popover is open (alongside the
  // warning-dialog hold via `warningDialogState`).
  const isDragActionPopoverOpen =
    shouldShowPopoverDialog.id !== '' && !!shouldShowPopoverDialog.isDragAction;

  // Frozen drop preview: hold a copy of the card snapped into the target cell +
  // faded source until the call + refetch settle. See `useFrozenDrop`.
  const { freeze: freezeCardDrop, release: releaseFrozenDrop } = useFrozenDrop(
    frozenCardDropAtom,
    {
      isLoading: loading,
      holdWhileOpen:
        isDragActionPopoverOpen ||
        (warningDialogState.open &&
          warningDialogState.type === DialogType.WARNING)
    }
  );

  const onMutationError = () => {
    containerRef.current?.removeAttribute('data-drag-active');
    setPayload(undefined);
    releaseFrozenDrop();
    revertActionRef.current();
    showErrorSnackbar();
    setPendingDropPosition(null);
  };

  const sendConnectSchedule = useConnectScheduleAction({
    connectSchedule,
    defaultScheduleStart: startDate,
    onError: onMutationError,
    planningEntities
  });

  // Ref-latch so the compiler-memoized `ActivityPanelPragmatic` isn't
  // re-rendered on every render (`sendConnectSchedule` re-identifies whenever
  // `planningEntities` changes).
  const sendConnectScheduleRef = useRef(sendConnectSchedule);
  useEffect(() => {
    sendConnectScheduleRef.current = sendConnectSchedule;
  });
  const stableSendConnectSchedule = (
    planningEntityIdentifier: PlanningEntityIdentifier,
    scheduleStart: string
  ) => {
    sendConnectScheduleRef.current(planningEntityIdentifier, scheduleStart);
  };

  function unassignAppointment(editAppointmentType: EditAppointmentType) {
    unassignAppointmentUtil({
      editAppointmentType,
      activeEvent,
      selectedIds,
      planningEntities,
      shouldShowPopoverDialog,
      appendCorrelationToken,
      unassignAppointmentAsync,
      setShouldShowPopoverDialog,
      setPlanResponsePlanningEntityIdentifiers,
      setActiveEntityContainingSelection,
      setIsLoading: setIsLoadingUnassignAppointmentFromEmployee,
      onFinally: () => {
        containerRef.current?.removeAttribute('data-drag-active');
        setIsLoadingUnassignAppointmentFromEmployee(false);
        setActiveEvent(null);
        setActiveEntityContainingSelection(null);
        setPendingDropPosition(null);
        setSelectedIds([]);
      },
      onGetEvents: (planningEntity, isToPlan, selectedIds) => {
        return (
          (isToPlan
            ? planningEntity?.planningTasks
                ?.map((task: any) => task.data)
                .filter(
                  (event: Event) =>
                    event?.id !== undefined && selectedIds.includes(event.id)
                )
            : planningEntity?.schedules
                .flatMap((s: any) => s.events)
                .filter(({ id }: { id: string }) => selectedIds.includes(id))
          )?.filter(
            (event: Event | undefined): event is Event => event !== undefined
          ) || []
        );
      }
    });
  }

  const handlePlanAppointments = (payload: PlanAppointmentsRequest | null) => {
    handlePlanAppointmentsUtil({
      payload,
      planAppointments,
      setPayload,
      setShouldShowPopoverDialog,
      setDialogState,
      revertAction,
      onMutationError,
      planningEntitiesToEvents,
      getConflictingEvents,
      notificationsToHashTable,
      planningEntities,
      appendCorrelationToken,
      setPlanResponsePlanningEntityIdentifiers,
      setPlanningEntities,
      viewport: {
        startDate,
        endDate,
        planningEntityIdentifiers: currentPlanningEntityIdentifiers
      },
      onCancelWarningDialog: () => {
        releaseFrozenDrop();
        setActiveEvent(null);
        setPendingDropPosition(null);
      },
      onSuccess: () => {
        containerRef.current?.removeAttribute('data-drag-active');
        setSelectedIds([]);
        setActiveEntityContainingSelection(null);
        setActiveEvent(null);
        setPendingDropPosition(null);
        // Frozen frame is released by the loading-settled effect, so it clears
        // exactly when the card snaps to its final position.
      },
      onComplete: () => {
        setSelectedIds([]);
      }
    });
  };

  // --- Stable handler refs to avoid stale closures in the monitor ---
  const handleDragStartRef = useRef<(source: any) => void>(() => {});
  const handleDragEndRef = useRef<(source: any, location: any) => void>(
    () => {}
  );

  // Combined Pragmatic monitor: handles drop dispatch via the stable refs AND
  // drives the vertical drag-driven autoscroll for the table scroll container.
  // Mirrors the autoscroll loop in RouteOverviewPragmatic; see its inline
  // comments for the rationale on the asymmetric top/bottom zones (downward
  // drag preview sits below the cursor, so the bottom zone is intentionally
  // larger).
  useEffect(() => {
    const el = scrollContainerRef.current;

    const SCROLL_ZONE_TOP_PX = 100;
    const SCROLL_ZONE_BOTTOM_PX = 160;
    const MAX_SCROLL_PX_PER_FRAME = 15;

    let rafId: number | null = null;
    let clientY = 0;

    function scrollStep() {
      if (!el) {
        rafId = null;
        return;
      }
      const rect = el.getBoundingClientRect();
      const fromTop = clientY - rect.top;
      const fromBottom = rect.bottom - clientY;

      let delta = 0;
      if (fromTop < SCROLL_ZONE_TOP_PX) {
        delta = -MAX_SCROLL_PX_PER_FRAME * (1 - fromTop / SCROLL_ZONE_TOP_PX);
      } else if (fromBottom < SCROLL_ZONE_BOTTOM_PX) {
        delta =
          MAX_SCROLL_PX_PER_FRAME * (1 - fromBottom / SCROLL_ZONE_BOTTOM_PX);
      }

      if (delta !== 0) el.scrollBy({ top: delta });
      rafId = requestAnimationFrame(scrollStep);
    }

    function stopScroll() {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    }

    const cleanupMonitor = monitorForElements({
      onDragStart: ({ source, location }) => {
        clientY = location.current.input.clientY;
        // Guard against double-start (e.g. Pragmatic re-emitting after an
        // error) so we never orphan a RAF loop.
        if (el && rafId === null) {
          rafId = requestAnimationFrame(scrollStep);
        }
        handleDragStartRef.current(source);
      },
      onDrag: ({ location }) => {
        clientY = location.current.input.clientY;
      },
      onDrop: ({ source, location }) => {
        stopScroll();
        handleDragEndRef.current(source, location);
      }
    });

    return () => {
      stopScroll();
      cleanupMonitor();
    };
  }, []);

  function handleDragStart(source: any) {
    const activePlanningEntityId = source.data?.planningEntityId;
    const activeId = source.data?.id;

    // Guard: ignore drags that don't carry our contract (e.g. external drags)
    if (!activePlanningEntityId || !activeId) return;

    containerRef.current?.setAttribute('data-drag-active', '');

    const scheduleId = source.data?.scheduleId;
    const activeIsToPlan = source.data?.isToPlan;

    const event = !activeIsToPlan
      ? planningEntities?.[activePlanningEntityId]?.schedules
          .map((s) => s.events)
          .flat()
          .find(({ id }) => id === activeId) ?? null
      : planningEntities?.[activePlanningEntityId]?.planningTasks
          ?.map((task) => task.data)
          .find((task): task is Event => (task as Event)?.id === activeId) ??
        null;

    setActiveEvent(event);
    setActiveEntityContainingSelection(scheduleId || activePlanningEntityId);
    setSelectedIds((prev) => (prev.includes(activeId) ? prev : []));
    setClonedPlanningEntities(planningEntities);
  }

  function handleDragEnd(source: any, location: any) {
    const dropTargets: any[] = location.current.dropTargets;

    // No drop target — treat as cancel
    if (dropTargets.length === 0) {
      containerRef.current?.removeAttribute('data-drag-active');
      if (clonedPlanningEntities) {
        setPlanningEntities(clonedPlanningEntities);
      }
      setActiveEvent(null);
      setPendingDropPosition(null);
      setClonedPlanningEntities(null);
      return;
    }

    const overData = dropTargets[0].data;
    const overElement = dropTargets[0].element as HTMLElement | undefined;
    const activeData = source.data;

    // Guard: ignore drags that don't carry our contract
    if (!activeData?.planningEntityId || !activeData?.id) return;

    const activePlanningEntityId = activeData.planningEntityId;
    const activeIsToPlan = activeData.isToPlan;
    const overIsToPlan = overData.isToPlan;
    const isDroppableCell = overData.isDroppableCell;
    const isDroppablePanelHeader = overData.isDroppablePanelHeader;

    const planningEntityIdentifier = overData.planningEntityIdentifier;

    // Bail out for any of the non-actionable drops:
    //  - dropped on the same "side" we came from (toPlan -> toPlan, or
    //    schedule -> schedule), which is a no-op move
    //  - the active source has no planning entity context
    //  - we never captured an activeEvent (drag from outside / desync)
    // All three reset the same state to avoid leaking stale selection/event
    // into the next drag.
    if (
      activeIsToPlan === overIsToPlan ||
      activePlanningEntityId === undefined ||
      !activeEvent
    ) {
      containerRef.current?.removeAttribute('data-drag-active');
      setActiveEvent(null);
      setPendingDropPosition(null);
      setSelectedIds([]);
      return;
    }

    const activeId = activeData.id;
    const ids = selectedIds.length ? selectedIds : [activeId];

    const selectedEvents: Event[] = ids
      .map((id) => findItem(planningEntities, activePlanningEntityId, id))
      .filter((e): e is Event => e != null);

    const occurrenceDate = activeEvent.start
      ? format(new Date(activeEvent.start), 'y-MM-dd')
      : startDate;

    const scheduleStart = overData.scheduleStart;

    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);

    const dropPayload: PlanAppointmentsRequest = {
      appointmentOccurrences:
        selectedIds.length === 0
          ? [
              {
                entityId: activeEvent.id,
                originalOccurrenceStart: activeEvent.start,
                schedulingLabel: activeEvent.schedulingLabel
              }
            ]
          : selectedEvents.map((event: Event) => ({
              entityId: event.id,
              originalOccurrenceStart: event.start,
              schedulingLabel: event.schedulingLabel
            })),
      planningEntity: planningEntityIdentifier,
      start:
        isDroppableCell || isDroppablePanelHeader
          ? null
          : overData.start || activeEvent?.start,
      date:
        (isDroppableCell || isDroppablePanelHeader) && scheduleStart
          ? dayjs(scheduleStart).format('YYYY-MM-DD')
          : '',
      editAppointmentType: 'ONLY_THIS',
      suppressWarnings: false,
      // The row being dragged out of. Completed with the board's dates and rows in handlePlanAppointments.
      viewport: sourceViewportContext(planningEntities, activePlanningEntityId)
    };

    // Freeze the last drag frame: hold a copy of the card snapped into the
    // target cell + faded source until the call + refetch settle. Snap to the
    // drop target's top-left so it lands where the drop indicator sat.
    const freezeDrop = () => {
      const rect = overElement?.getBoundingClientRect();
      const input = location.current.input;
      const position = rect
        ? { x: rect.left, y: rect.top }
        : { x: input.clientX, y: input.clientY };
      freezeCardDrop({
        eventIds: ids,
        position,
        width: rect?.width ?? 0,
        count: ids.length,
        event: activeEvent,
        planningEntityId: activePlanningEntityId,
        planningEntityType: activeData.planningEntityType
      });
    };

    if (activeEvent?.isRecurring && new Date(occurrenceDate) >= today) {
      // Recurring: freeze the card in the target cell and keep it there while
      // the confirmation popover is open (held via `isDragActionPopoverOpen`),
      // so it doesn't vanish before the user picks an occurrence option.
      freezeDrop();
      // Un-dim the other cards immediately (popover is about to show); the
      // frozen source stays faded on its own.
      containerRef.current?.removeAttribute('data-drag-active');
      const input = location.current.input;
      setPayload(dropPayload);
      setPendingDropPosition({ x: input.clientX, y: input.clientY });
      setShouldShowPopoverDialog({
        id: activeEvent.id,
        scheduleStart: scheduleStart
          ? dayjs(scheduleStart).format('YYYY-MM-DD')
          : '',
        isUnassignment: overIsToPlan,
        isDragAction: true
      });
    } else if (!activeEvent?.isRecurring && overIsToPlan) {
      freezeDrop();
      unassignAppointment('ONLY_THIS');
    } else {
      freezeDrop();
      handlePlanAppointments(dropPayload);
    }
  }

  // Refs updated in a layout effect (never during render) so the React Compiler
  // can optimize this component; useLayoutEffect runs synchronously after
  // commit, so the monitor never dispatches into a stale `.current` right after
  // a render.
  useLayoutEffect(() => {
    handleDragStartRef.current = handleDragStart;
    handleDragEndRef.current = handleDragEnd;
  });

  return (
    <PopOverConfirmationContext.Provider
      value={{
        isLoading: loading,
        onClose: () => {
          releaseFrozenDrop();
          setActiveEvent(null);
          setPendingDropPosition(null);
          revertAction();
        },
        onHandleAppointmentConfirmation: (
          editAppointmentType: EditAppointmentType,
          isUnassignment?: boolean
        ) => {
          if (isUnassignment) {
            return unassignAppointment(editAppointmentType);
          }

          if (payload && 'appointmentOccurrences' in payload) {
            handlePlanAppointments({
              ...(payload as PlanAppointmentsRequest),
              editAppointmentType
            });
          } else {
            connectAppointmentGrouping(
              {
                ...payload,
                editAppointmentType
              } as ConnectAppointmentGroupingPayload,
              {
                onSuccess: (response: any) => {
                  if (
                    !response.data.success &&
                    payload &&
                    'appointmentGroupingId' in payload &&
                    planningEntities
                  ) {
                    const events =
                      planningEntities[payload.appointmentGroupingId].schedules
                        .map((s) => s.events)
                        .flat() ?? [];
                    const notifications = response.data
                      .notifications as APNotification[];
                    const conflictingEvents: Event[] = getConflictingEvents(
                      events || [],
                      notifications
                    );

                    setDialogState({
                      type: DialogType.WARNING,
                      open: true,
                      data: notificationsToHashTable(
                        conflictingEvents,
                        notifications
                      ),
                      onSubmit: () => {
                        connectAppointmentGrouping(
                          {
                            ...payload,
                            editAppointmentType,
                            suppressWarnings: true
                          } as ConnectAppointmentGroupingPayload,
                          {
                            onError: onMutationError
                          }
                        );
                      },
                      onCancel: () => {
                        revertAction();
                        setActiveEvent(null);
                      }
                    });
                  } else {
                    setActiveEvent(null);
                  }
                },
                onError: onMutationError
              }
            );
          }
        }
      }}
    >
      <LoadingOverlay loading={loading} zIndex={DROP_LOADING_OVERLAY_Z_INDEX} />
      <Box
        ref={containerRef}
        display="flex"
        height="100%"
        overflow="hidden"
        position="relative"
        sx={{
          // Infinite header bar for table in case of large screens
          '&:before': {
            content: '""',
            backgroundColor: GRAY_LIGHT_TERTIARY,
            boxShadow: `inset 0 -1px 0 ${PRIMARY}`,
            width: '100%',
            height: '50px',
            position: 'absolute',
            top: 0
          }
        }}
      >
        {/* Scroll container */}
        <Box
          ref={scrollContainerRef}
          sx={{
            overflowX: 'scroll',
            overflowY: 'auto',
            height: '100%',
            minWidth: taskListIsOpen ? 'initial' : '100%',
            position: 'relative',
            borderLeft: (theme) => `1px solid ${theme.palette.divider}`
          }}
        >
          <Box display="flex" position="relative" width="100%">
            {/* Table */}
            {normalizedData && (
              <Table sx={{ tableLayout: 'auto', borderCollapse: 'collapse' }}>
                <tbody>
                  {/* Header row */}
                  <TableHeaderDateRow>
                    {/* Week number */}
                    <TableHeaderDateCell>
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        sx={{
                          position: 'fixed',
                          transform: 'translateY(-50%)',
                          left: '16px',
                          bgcolor: 'text.primary',
                          borderRadius: '50%',
                          color: 'white',
                          width: 26,
                          height: 26
                        }}
                      >
                        {weekNumber}
                      </Box>
                    </TableHeaderDateCell>

                    {/* Days */}
                    {weekdays.map((w: RosterDay, i) => (
                      <TableHeaderDateCell key={i}>
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                        >
                          {(w.isToday || w.isHoliday) && (
                            <Tooltip
                              title={w.description}
                              arrow
                              enterDelay={300}
                              enterNextDelay={300}
                            >
                              <Chip
                                shape="pill"
                                variant="filled"
                                label={`${w.dayName} ${w.day}`}
                                color={w.isToday ? 'primary' : 'warning'}
                              />
                            </Tooltip>
                          )}
                          {!(w.isHoliday || w.isToday) &&
                            `${w.dayName} ${w.day}`}
                        </Box>
                      </TableHeaderDateCell>
                    ))}
                  </TableHeaderDateRow>

                  {hasKidsPlanAccess &&
                    !showEvents &&
                    kidsRatio &&
                    kidsRatio.totalRatios &&
                    kidsRatio.totalRatios.length > 0 &&
                    planningEntityType === 'EMPLOYEE' && (
                      <KidsTable ratios={kidsRatio} />
                    )}
                  {normalizedData &&
                    normalizedData.length > 0 &&
                    normalizedData.map((item, i: number) => {
                      const previousRow = normalizedData?.[i - 1];
                      return Array.from({
                        length: item.rowCount > 0 ? item.rowCount : 1
                      }).map((_, idx) => {
                        return (
                          <tr
                            key={`${item.id}${idx}`}
                            {...(previousRow &&
                              item.type !== previousRow?.type && {
                                style: {
                                  borderTop: `1px solid ${PRIMARY}`
                                }
                              })}
                          >
                            {idx === 0 && (
                              <TableCell
                                style={{
                                  verticalAlign: 'top',
                                  minWidth: '100px',
                                  position: 'sticky',
                                  left: 0,
                                  zIndex: 30,
                                  background: `linear-gradient(90deg, var(--mui-palette-background-default) 70%, transparent)`,
                                  padding: '0 8px'
                                }}
                                rowSpan={item.rowCount > 0 ? item.rowCount : 1}
                              >
                                {idx === 0 && (
                                  <Box justify-content="space-between">
                                    <Box mt={1}>
                                      <Typography>
                                        {item?.name ?? 'Geen medewerker'}
                                      </Typography>

                                      {item?.description && (
                                        <Typography variant="subtitle2">
                                          {item.description}
                                        </Typography>
                                      )}
                                    </Box>
                                    {item?.metrics &&
                                      planningEntityType === 'EMPLOYEE' && (
                                        <Box
                                          mt={1}
                                          sx={{ marginTop: '-5px' }}
                                          display="flex"
                                        >
                                          <Typography variant="subtitle2">
                                            Contract:{' '}
                                            {formatHours(
                                              item.metrics
                                                .contractDurationInSeconds ?? 0
                                            )}
                                          </Typography>
                                          <Box width={'20px'} />
                                          <Typography variant="subtitle2">
                                            Gepland:{' '}
                                            {formatHours(
                                              item.metrics
                                                .plannedDurationInSeconds ?? 0
                                            )}
                                          </Typography>
                                        </Box>
                                      )}
                                  </Box>
                                )}
                              </TableCell>
                            )}
                            {Array.from({ length: weekLength }).map(
                              (_, weekday) => {
                                const schedule: Schedule =
                                  item.schedules?.[weekday + 1]?.[idx];
                                const currentDay = dayjs(startDate).add(
                                  weekday,
                                  'day'
                                );
                                const absence: AbsenceResponse =
                                  item.absences?.[
                                    currentDay.format('DD-MM-YYYY')
                                  ];

                                const availability: AvailabilityResponse =
                                  item.availabilities?.[
                                    currentDay.format('DD-MM-YYYY')
                                  ];

                                const showAvailability =
                                  availability &&
                                  availability?.type === 'NOT_AVAILABLE';

                                // For <DroppableCellPragmatic/> and <AbsencePragmatic/>
                                const getDroppableProps = (id: string) => ({
                                  id: `${id}-${weekday}-${idx}-${item.id}`,
                                  planningEntityId: item.id,
                                  planningEntityIdentifier:
                                    item.planningEntityIdentifier!,
                                  planningEntityType: item.type,
                                  scheduleStart: dayjs(startDate)
                                    .add(weekday, 'day')
                                    .format('YYYY-MM-DD')
                                });

                                if (
                                  showEvents &&
                                  planningEntityType === 'EMPLOYEE'
                                ) {
                                  if (schedule || absence || showAvailability) {
                                    return (
                                      <TableCell
                                        key={`${idx}-${weekday}`}
                                        style={{
                                          minWidth: `${CARD_WIDTH}px`,
                                          verticalAlign: 'top'
                                        }}
                                      >
                                        {absence && (
                                          <AbsencePragmatic
                                            {...getDroppableProps('absence')}
                                            absence={absence}
                                            showTime={
                                              Math.abs(
                                                dayjs(absence.start).diff(
                                                  absence.end,
                                                  'minutes'
                                                )
                                              ) <
                                                60 * 24 - 1 ||
                                              (currentDay.isoWeekday() ===
                                                dayjs(
                                                  absence.start
                                                ).isoWeekday() &&
                                                isWithinDay(absence.start)) ||
                                              (currentDay.isoWeekday() ===
                                                dayjs(
                                                  absence.end
                                                ).isoWeekday() &&
                                                isWithinDay(absence.end))
                                            }
                                          />
                                        )}

                                        {showAvailability && (
                                          <AvailabilityPragmatic
                                            {...getDroppableProps(
                                              'availability'
                                            )}
                                            availability={availability}
                                            showTime={
                                              Math.abs(
                                                dayjs(availability.start).diff(
                                                  availability.end,
                                                  'minutes'
                                                )
                                              ) <
                                                60 * 24 - 1 ||
                                              (currentDay.isoWeekday() ===
                                                dayjs(
                                                  availability.start
                                                ).isoWeekday() &&
                                                isWithinDay(
                                                  availability.start
                                                )) ||
                                              (currentDay.isoWeekday() ===
                                                dayjs(
                                                  availability.end
                                                ).isoWeekday() &&
                                                isWithinDay(availability.end))
                                            }
                                          />
                                        )}

                                        <Box
                                          sx={{
                                            height:
                                              absence && showAvailability
                                                ? 'calc(100% - 100px)'
                                                : absence || showAvailability
                                                ? 'calc(100% - 50px)'
                                                : '100%'
                                          }}
                                        >
                                          {schedule && (
                                            <ActivityPanelPragmatic
                                              id={`${weekday}-${idx}-${item.id}`}
                                              key={`${weekday}-${idx}-${item.id}`}
                                              readOnly={
                                                !hasActivityPlanningEditAccess
                                              }
                                              name={schedule.name}
                                              headerName={schedule.name}
                                              showDuty={false}
                                              showDurationDistanceTransport={
                                                isDetailed
                                              }
                                              {...schedule}
                                              planningEntityType={item.type}
                                              planningEntityId={
                                                item.planningEntityIdentifier
                                                  ?.entityId ??
                                                item.planningEntityIdentifier
                                                  ?.externalEntityId ??
                                                item.id ??
                                                ''
                                              }
                                              planningEntityIdentifier={
                                                item.planningEntityIdentifier
                                              }
                                              isOverrulingExternalDuty={
                                                schedule.isOverrulingExternalDuty
                                              }
                                              activeEntityContainingSelection={
                                                activeEntityContainingSelection
                                              }
                                              onChangeActiveEntity={
                                                setActiveEntityContainingSelection
                                              }
                                              onChangeSelection={setSelectedIds}
                                              onConnectSchedule={
                                                stableSendConnectSchedule
                                              }
                                            />
                                          )}
                                        </Box>
                                      </TableCell>
                                    );
                                  }

                                  return (
                                    <TableCell
                                      key={`${idx}-${weekday}`}
                                      style={{
                                        minWidth: `${CARD_WIDTH}px`
                                      }}
                                    >
                                      {item &&
                                      planningEntityType === 'EMPLOYEE' ? (
                                        <DroppableCellPragmatic
                                          {...getDroppableProps('cell')}
                                        />
                                      ) : (
                                        <EventPlaceholder
                                          key={`${idx}-${weekday}`}
                                          date={dayjs(startDate)
                                            .add(weekday, 'day')
                                            .toISOString()}
                                        />
                                      )}
                                    </TableCell>
                                  );
                                }

                                let event =
                                  showEvents ||
                                  planningEntityType !== 'EMPLOYEE'
                                    ? item.events?.[weekday + 1]?.[idx]
                                    : item.schedules?.[weekday + 1]?.[idx];
                                if (
                                  planningEntityType === 'PROJECT' &&
                                  item.type === 'EMPLOYEE'
                                ) {
                                  event = item.schedules?.[weekday + 1]?.[idx];
                                }

                                const isPlannedForDate = dayjs(
                                  (event as Event)?.date
                                ).isSame(
                                  dayjs(startDate).add(weekday, 'day'),
                                  'date'
                                );

                                if (!isPlannedForDate) {
                                  event = null;
                                }

                                const subtitle = getSubtitle(
                                  event,
                                  planningEntityType,
                                  showEvents
                                );

                                const cellScheduleStart = dayjs(startDate)
                                  .add(weekday, 'day')
                                  .format('YYYY-MM-DD');

                                return (
                                  <TableCell
                                    key={`${idx}-${weekday}`}
                                    style={{
                                      minWidth: `${CARD_WIDTH}px`
                                    }}
                                  >
                                    {(() => {
                                      if (event) {
                                        switch (planningEntityType) {
                                          case 'PROJECT':
                                            if (item.type === 'PROJECT') {
                                              return (
                                                <DraggableCardPragmatic
                                                  {...event}
                                                  planningEntityId={item.id}
                                                  planningEntityType={item.type}
                                                  scheduleStart={
                                                    cellScheduleStart
                                                  }
                                                  component="div"
                                                  isAlwaysActivityCard
                                                  copyMode={copyMode}
                                                />
                                              );
                                            }
                                            return (
                                              <EventCard
                                                title={
                                                  event.duty?.dutyCode?.code
                                                }
                                                subtitle={subtitle}
                                                event={event}
                                                showActions={false}
                                              />
                                            );
                                          default:
                                            if (showEvents) {
                                              return (
                                                <DraggableCardPragmatic
                                                  {...event}
                                                  planningEntityId={item.id}
                                                  planningEntityType={item.type}
                                                  scheduleStart={
                                                    cellScheduleStart
                                                  }
                                                  component="div"
                                                  isAlwaysActivityCard
                                                  copyMode={copyMode}
                                                />
                                              );
                                            }
                                            return (
                                              <EventCard
                                                title={
                                                  event.duty?.dutyCode?.code
                                                }
                                                multiplePlanUnitsSelected={
                                                  multiplePlanUnitsSelected
                                                }
                                                subtitle={subtitle}
                                                event={event}
                                                showActions={false}
                                              />
                                            );
                                        }
                                      } else {
                                        // Placeholders
                                        switch (planningEntityType) {
                                          case 'WORKPLACE':
                                            return (
                                              <EventPlaceholder
                                                workPlaceEntityId={item?.id}
                                                date={dayjs(startDate)
                                                  .add(weekday, 'day')
                                                  .toISOString()}
                                              />
                                            );
                                          case 'PROJECT':
                                            return (
                                              <EventPlaceholder
                                                projectEntityId={item?.id}
                                                date={dayjs(startDate)
                                                  .add(weekday, 'day')
                                                  .toISOString()}
                                              />
                                            );
                                          default:
                                            return (
                                              <EventPlaceholder
                                                date={dayjs(startDate)
                                                  .add(weekday, 'day')
                                                  .toISOString()}
                                              />
                                            );
                                        }
                                      }
                                    })()}
                                  </TableCell>
                                );
                              }
                            )}
                          </tr>
                        );
                      });
                    })}
                </tbody>
              </Table>
            )}
          </Box>
        </Box>

        {planningEntityType === 'EMPLOYEE' && showEvents && (
          <TaskList
            onOpen={setTaskListIsOpen.bind(undefined, true)}
            onClose={setTaskListIsOpen.bind(undefined, false)}
            planningEntities={planningEntities}
            dropzonePadding="10px"
            activeEntityContainingSelection={activeEntityContainingSelection}
            onChangeActiveEntity={setActiveEntityContainingSelection}
            onChangeSelection={setSelectedIds}
            DropzoneComponent={ToPlanDropzonePragmatic}
            SortedToPlanListComponent={SortedToPlanListPragmatic}
          />
        )}
      </Box>
      {activeEvent && pendingDropPosition && (
        <DragPortalPragmatic
          activeEvent={activeEvent}
          selectedIds={selectedIds}
          pendingDropPosition={pendingDropPosition}
        />
      )}
      <FrozenDropCardPragmatic />
    </PopOverConfirmationContext.Provider>
  );
}
